import React, { useEffect, useMemo, useState } from 'react';
import { nativePrintTarget, resolvePrinterForElectron } from '../../utils/printTarget';
import { Combination, Floor, Order, OrderProduct, Printer, Product } from '../../interfaces';
import { formatPrice } from '../../utils/formatPrice';
import { printKitchenTickets } from '../../services/KitchenPrintService';
import './SendProductsPopup.css';

interface SendProductsPopupProps {
    onClose: () => void;
    onSent: () => void;
    order: Order | undefined;
    floor: Floor | null;
    products: Product[];
    combinations: Combination[];
}

const formatDateForReceipt = (date: Date): string =>
    new Date(date).toLocaleString('en-US', {
        weekday: 'short', month: 'long', day: '2-digit', year: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit',
    });

const PrinterIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6v-8Z" /></svg>
);

type Phase = 'review' | 'sending' | 'sent';

const SendProductsPopup: React.FC<SendProductsPopupProps> = ({ onClose, onSent, order, floor, products, combinations }) => {
    const [phase, setPhase] = useState<Phase>('review');
    const [printers, setPrinters] = useState<Printer[]>([]);
    const [sentIds, setSentIds] = useState<string[]>([]);
    const [sentStations, setSentStations] = useState<string[]>([]);
    const [printError, setPrintError] = useState<string | null>(null);
    // Printer groups that already printed OK this session — so a retry after a partial
    // failure doesn't reprint (double) the tickets that already succeeded.
    const [succeededPrinterIds, setSucceededPrinterIds] = useState<string[]>([]);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/Printer`, { method: 'GET', credentials: 'include' })
            .then(r => r.ok ? r.json() : [])
            .then(data => setPrinters(data))
            .catch(() => { });
    }, []);

    const findSubproductDef = (comboDef: Combination | undefined, subId: string) => {
        if (!comboDef) return undefined;
        if (comboDef.type === 0) {
            return comboDef.subproducts?.find(sp => sp.id === subId);
        }
        for (const cat of comboDef.subproductCategories ?? []) {
            const found = cat.products?.find(p => p.id === subId);
            if (found) return found;
        }
        return undefined;
    };

    const { printerGroups, unassigned, comboRouting } = useMemo(() => {
        const unsentProducts = order?.products.filter(p => !p.sentToKitchen) ?? [];
        const unsentCombinations = order?.combinations.filter(c => !c.sentToKitchen) ?? [];

        const groups: Record<string, { printerName: string; items: OrderProduct[] }> = {};
        const unassigned: OrderProduct[] = [];

        unsentProducts.forEach(item => {
            const mapping = floor?.categoryPrinters?.find(cp => cp.productCategoryId === item.categoryId && cp.printerId);
            if (mapping) {
                const key = mapping.printerId!;
                if (!groups[key]) groups[key] = { printerName: mapping.printerName ?? key, items: [] };
                groups[key].items.push(item);
            } else {
                unassigned.push(item);
            }
        });

        const comboRouting = unsentCombinations.map(orderCombo => {
            const comboDef = combinations.find(c => c.id === orderCombo.combinationId);
            const subproductRouting = (orderCombo.selectedsubproductsIds ?? [])
                .map((subId, idx) => {
                    const subproductDef = findSubproductDef(comboDef, subId);
                    if (!subproductDef || (!subproductDef.productId && !subproductDef.productName)) return null;
                    const product = subproductDef.productId
                        ? products.find(p => p.id === subproductDef.productId)
                        : products.find(p => p.name === subproductDef.productName);
                    const mapping = product
                        ? floor?.categoryPrinters?.find(cp => cp.productCategoryId === product.categoryId && cp.printerId)
                        : undefined;
                    return {
                        name: subproductDef.productName ?? orderCombo.selectedsubproductsNames?.[idx] ?? subId,
                        printerName: mapping?.printerName ?? null,
                    };
                })
                .filter((x): x is { name: string; printerName: string | null } => x !== null);
            return { combo: orderCombo, subproductRouting };
        });

        return { printerGroups: groups, unassigned, comboRouting };
    }, [order, floor, products, combinations]);

    const totalUnsent =
        Object.values(printerGroups).reduce((s, g) => s + g.items.length, 0) +
        unassigned.length +
        comboRouting.length;

    const handlePreview = async () => {
        if (!order) return;
        const productIds = [
            ...Object.values(printerGroups).flatMap(g => g.items.map(i => i.id)),
            ...unassigned.map(i => i.id),
        ];
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/Order/preview-captain-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order, productsToBePrintedIds: productIds, formattedDate: formatDateForReceipt(new Date()) }),
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Preview failed');
            const html = await res.text();
            const w = window.open('', '_blank');
            if (w) { w.document.write(html); w.document.close(); }
        } catch (e) {
            console.error('Captain order preview failed:', e);
        }
    };

    // Print one printer group's captain ticket. Returns true on success, false on failure.
    // Kept inline (rather than delegating to printKitchenTickets) so the send path can report
    // per-printer success/failure and offer staff a retry without double-printing.
    const printGroupTicket = async (
        Capacitor: any,
        printerId: string,
        group: { printerName: string; items: OrderProduct[] },
    ): Promise<boolean> => {
        const fullPrinter = printers.find(p => p.id === printerId);
        if (!fullPrinter) return false;
        const productIds = group.items.map(item => item.id);

        // Generate kitchen ticket HTML — API returns the HTML directly now (no blob round-trip);
        // fall back to local generation when offline / the API is unreachable.
        let ticketHtmlContent = '';
        try {
            const genResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/Order/generate-kitchen-order`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        order: order,
                        productsToBePrintedIds: productIds,
                        formattedDate: formatDateForReceipt(new Date()),
                    }),
                    credentials: 'include',
                }
            );
            if (!genResponse.ok) throw new Error('API failed');
            ticketHtmlContent = await genResponse.text();
        } catch {
            const { default: ReceiptService } = await import('../../services/ReceiptService');
            ticketHtmlContent = await ReceiptService.generateCaptainOrderHtml(order, formatDateForReceipt(new Date()), productIds);
        }

        try {
            if ((window as any).electronAPI?.printThermalReceipt) {
                const blob = new Blob([ticketHtmlContent], { type: 'text/html' });
                const blobUrl = URL.createObjectURL(blob);
                const result = await window.electronAPI.printThermalReceipt(blobUrl, { ...order, printer: resolvePrinterForElectron(fullPrinter) });
                URL.revokeObjectURL(blobUrl);
                if (!result?.success) throw new Error(result?.error || 'print failed');
            } else {
                const { ThermalPrinter } = (Capacitor as any).Plugins;
                if (!ThermalPrinter) throw new Error('ThermalPrinter plugin not available');
                await ThermalPrinter.printHtml({
                    htmlContent: ticketHtmlContent,
                    ...nativePrintTarget(fullPrinter),
                    port: fullPrinter.port || 9100,
                    ticketWidthMm: fullPrinter.ticketWidth || 80,
                    autoCutter: fullPrinter.hasAutoCutter ?? true,
                    printerType: fullPrinter.type === 'Star Micronics' ? 'STAR' : 'EPSON',
                });
            }
            return true;
        } catch (e: any) {
            console.error(`Native print failed for ${fullPrinter.name}:`, e?.message || e);
            return false;
        }
    };

    const handleSend = async () => {
        // Guard against re-entrant taps while a (possibly slow) print is in flight — this is
        // what caused duplicate kitchen tickets when staff tapped Send again.
        if (!order?.id || phase === 'sending') return;
        setPhase('sending');
        setPrintError(null);

        const productIds = [
            ...Object.values(printerGroups).flatMap(g => g.items.map(i => i.id)),
            ...unassigned.map(i => i.id),
        ];
        const stations = Object.values(printerGroups).map(g => g.printerName);

        try {
            const { Capacitor } = await import('@capacitor/core');
            const canPrint = !!(window as any).electronAPI?.printThermalReceipt || Capacitor.isNativePlatform();

            const failed: string[] = [];
            const justSucceeded: string[] = [];

            if (canPrint) {
                // Skip groups that already printed OK this session so a retry doesn't double-print.
                const pending = Object.entries(printerGroups).filter(([pid]) => !succeededPrinterIds.includes(pid));
                // Print all printers in parallel (distinct IPs) instead of sequentially.
                const results = await Promise.all(
                    pending.map(async ([printerId, group]) => ({
                        printerId,
                        name: group.printerName,
                        ok: await printGroupTicket(Capacitor, printerId, group),
                    }))
                );
                for (const r of results) {
                    if (r.ok) justSucceeded.push(r.printerId);
                    else failed.push(r.name);
                }
                if (justSucceeded.length) setSucceededPrinterIds(prev => [...prev, ...justSucceeded]);
            }

            // A print failed — surface it and DO NOT mark the order sent or advance. Staff can fix
            // the printer and tap Send again; already-printed tickets won't reprint.
            if (failed.length > 0) {
                setPrintError(`Couldn't print to: ${failed.join(', ')}. Check the printer, then tap Send to retry.`);
                setPhase('review');
                return;
            }

            // All tickets printed (or nothing to print) — mark products as sent (with offline fallback).
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/send-order-to-kitchen/${order.id}`, { method: 'GET', credentials: 'include' });
                if (!response.ok) throw new Error('API failed');
            } catch {
                const { enqueueMutation } = await import('../../services/OfflineDb');
                await enqueueMutation({
                    type: 'SEND_TO_KITCHEN',
                    url: `${process.env.REACT_APP_API_URL}/Order/send-order-to-kitchen/${order.id}`,
                    method: 'GET',
                    body: '',
                });
            }

            setSentIds(productIds);
            setSentStations(stations);
            setPhase('sent');
        } catch (err) {
            console.error(err);
            setPrintError('Something went wrong while sending. Please try again.');
            setPhase('review');
        }
    };

    const handleReprint = async () => {
        if (!order || sentIds.length === 0) return;
        await printKitchenTickets({ order, floor, productIds: sentIds, printers });
    };

    return (
        <div className="spk-scrim" onMouseDown={() => phase !== 'sending' && onClose()}>
            <div className="spk-sheet" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true">
                {phase === 'sent' ? (
                    <div className="spk-done">
                        <div className="spk-donecheck">
                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        </div>
                        <div className="spk-donetitle">Sent to the kitchen</div>
                        <div className="spk-donetext">
                            {sentIds.length} item{sentIds.length !== 1 ? 's' : ''} fired
                            {sentStations.length > 0 ? <> to <b>{sentStations.join(', ')}</b></> : null}. Tickets are printing now.
                        </div>
                        <div className="spk-doneactions">
                            <button className="spk-btn spk-btn--ghost" onClick={handleReprint}>Reprint ticket</button>
                            <button className="spk-btn spk-btn--primary" onClick={onSent}>Done</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="spk-head">
                            <div>
                                <div className="spk-title">Send to kitchen</div>
                                <div className="spk-sub">{order?.tableName ? <>Table <b>{order.tableName}</b> · </> : null}routed by station</div>
                            </div>
                            <button className="spk-x" onClick={onClose} aria-label="Close" disabled={phase === 'sending'}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M13 1 1 13M1 1l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>

                        {totalUnsent > 0 && (
                            <div className="spk-summary">
                                <span className="spk-summary__count">{totalUnsent}</span>
                                <span>new item{totalUnsent !== 1 ? 's' : ''} ready to fire</span>
                            </div>
                        )}

                        {printError && (
                            <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '0 20px 12px', padding: '10px 12px', border: '1px solid #FDA29B', background: '#FEF3F2', borderRadius: 6, color: '#B42318', fontSize: 13 }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                                    <circle cx="12" cy="12" r="10" stroke="#D92D20" strokeWidth="2" />
                                    <path d="M12 8V12M12 16H12.01" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                <span>{printError}</span>
                            </div>
                        )}

                        <div className="spk-body">
                            {totalUnsent === 0 ? (
                                <div className="spk-empty">No pending products to send.</div>
                            ) : (
                                <>
                                    {Object.entries(printerGroups).map(([printerId, group]) => (
                                        <div className="spk-group" key={printerId}>
                                            <div className="spk-grouphead">
                                                <span className="spk-station">{PrinterIcon}{group.printerName}</span>
                                                <span className="spk-grouptag">{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
                                            </div>
                                            {group.items.map((l, i) => <LineRow key={i} item={l} />)}
                                        </div>
                                    ))}

                                    {comboRouting.length > 0 && (
                                        <div className="spk-group">
                                            <div className="spk-grouphead">
                                                <span className="spk-station spk-station--combo">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h18M3 12h18M3 17h18" /></svg>
                                                    Combinations
                                                </span>
                                                <span className="spk-grouptag">{comboRouting.length} item{comboRouting.length !== 1 ? 's' : ''}</span>
                                            </div>
                                            <div className="spk-combobox">
                                                {comboRouting.map(({ combo, subproductRouting }, i) => (
                                                    <div className="spk-comboitem" key={i}>
                                                        <div className="spk-comborowhead">
                                                            <span className="spk-comboname">{combo.combinationName}</span>
                                                            <span className="spk-grouptag">{formatPrice(combo.price)}</span>
                                                        </div>
                                                        {subproductRouting.map((sub, si) => (
                                                            <div className="spk-comborow" key={si}>
                                                                <span className="spk-subname">{sub.name}</span>
                                                                <span className={`spk-route${sub.printerName ? '' : ' spk-route--none'}`}>
                                                                    {sub.printerName ? <>{PrinterIcon}{sub.printerName}</> : 'No station'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                        {combo.note ? <div className="spk-linenote">Note: {combo.note}</div> : null}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {unassigned.length > 0 && (
                                        <div className="spk-group spk-group--warn">
                                            <div className="spk-grouphead">
                                                <span className="spk-station spk-station--warn">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01" /></svg>
                                                    No station assigned
                                                </span>
                                                <span className="spk-grouptag">{unassigned.length} item{unassigned.length !== 1 ? 's' : ''}</span>
                                            </div>
                                            {unassigned.map((l, i) => <LineRow key={i} item={l} />)}
                                            <div className="spk-warnnote">Set a category → printer rule to route these automatically.</div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="spk-foot">
                            <button className="spk-btn spk-btn--ghost" onClick={handlePreview} disabled={totalUnsent === 0 || phase === 'sending'}>Preview ticket</button>
                            <button className="spk-btn spk-btn--primary" onClick={handleSend} disabled={totalUnsent === 0 || phase === 'sending'}>
                                {phase === 'sending'
                                    ? <span className="spk-spin" />
                                    : <><svg width="16" height="16" viewBox="0 0 27 28" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M11.27 15.88 24.39 2.75M11.43 16.29l3.28 8.44c.29.75.44 1.12.65 1.23.18.1.39.1.57 0 .21-.1.36-.48.65-1.22l8.24-21.11c.26-.67.39-1.01.32-1.22a.64.64 0 0 0-.4-.4c-.21-.07-.55.06-1.22.32L2.41 10.57c-.74.29-1.11.44-1.22.65-.1.18-.1.39 0 .57.1.21.48.36 1.23.65l8.44 3.28Z" /></svg>{printError ? `Retry${totalUnsent > 0 ? ` (${totalUnsent})` : ''}` : (totalUnsent > 0 ? `Send ${totalUnsent} to kitchen` : 'Send')}</>}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const LineRow: React.FC<{ item: OrderProduct }> = ({ item }) => (
    <div className="spk-line">
        <span className="spk-qty">{item.qty}×</span>
        <div className="spk-linemain">
            <span className="spk-linename">{item.name}</span>
            {item.variants?.map((v, vi) => <span className="spk-linemod" key={`v${vi}`}>{v.name}</span>)}
            {item.addons?.map((a, ai) => <span className="spk-linemod" key={`a${ai}`}>+ {a.addon?.name}</span>)}
            {item.note ? <span className="spk-linenote">Note: {item.note}</span> : null}
        </div>
        <span className="spk-plu">{item.product?.plu}</span>
    </div>
);

export default SendProductsPopup;
